import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import appConst from "../../Utilities/appConst.json";
import { applyPreparedBasketItems, updateBasketItem } from "./helpers";
import type {
  BasketItemFull,
  BasketPayload,
  BasketState,
  CompanyDetails,
  Coupons,
  Delivery,
  Frame,
  InvoiceDetails,
  Payments,
  PersonalDetails,
} from "./types";

// ── Initial State ──────────────────────────────────────────────────────────────

const initialState: BasketState = {
  basket: {},
  basketItems: [],
  productType: appConst.TILES,
  totalCopiesAdditionOrRemoved: 0,
  copyAdditionOrRemoval: 0,
  frames: [],
  shortPathUpgradePrice: 0,
  orderID: "",
  stripeText: "",
  creditGuardURL: "",
  isNewsletter: false,
  tilesQuantityStripeText: "",
  tilesQuantityStripeMax: 0,
  tilesQuantityStripeMin: 0,
  showAlert: false,
  isMarketingStripeVisible: false,
  isBasketToolbarVisible: false,
  isShippingAlertVisible: false,
};

// ── Slice ──────────────────────────────────────────────────────────────────────

const basketSlice = createSlice({
  name: "basket",
  initialState,
  reducers: {
    // Sets the full basket payload from the API and recalculates all prices.
    setBasketItems: (state, action: PayloadAction<{
      payload: BasketPayload;
      product: string;
      isNewsletter?: boolean;
    }>) => {
      state.productType = action.payload.product;
      state.isNewsletter = !!action.payload.isNewsletter;
      state.basket = { ...action.payload.payload };
      state.orderID = action.payload.payload.order_id ?? "";
      applyPreparedBasketItems(
        state,
        action.payload.payload.items ?? [],
        { resetChecked: true }
      );
    },

    // Updates frame URLs and recalculates tile items if frames change.
    setFrames: (state, action: PayloadAction<Frame[]>) => {
      state.frames = [...action.payload];
      if (
        state.productType === appConst.TILES &&
        state.basketItems.length > 0
      ) {
        applyPreparedBasketItems(state, state.basketItems);
      }
    },

    // Replaces basket items and recalculates all prices.
    updateBasketItems: (state, action: PayloadAction<BasketItemFull[] | undefined>) => {
      if (action.payload) {
        applyPreparedBasketItems(state, action.payload);
      }
    },

    setBasketItemsPrice: (state, action: PayloadAction<number>) => {
      // Legacy: attaches item_price directly on the array object
      Object.assign(state.basketItems, { item_price: action.payload });
    },

    // Updates quantity for a single item and recalculates all prices.
    setBasketItemsQuantity: (state, action: PayloadAction<{
      index: number;
      value: number;
    }>) => {
      const existingItem = state.basketItems.find(
        (item) => item.item_id === action.payload.index
      );

      if (!existingItem) {
        return;
      }

      const copyAdditionOrRemoval =
        action.payload.value - existingItem.quantity;
      const quantity = action.payload.value;
      if (existingItem.isChecked) {
        state.totalCopiesAdditionOrRemoved += copyAdditionOrRemoval;
        state.copyAdditionOrRemoval = copyAdditionOrRemoval;
      }
      const nextItems = updateBasketItem(
        state.basketItems,
        action.payload.index,
        { quantity }
      );
      applyPreparedBasketItems(state, nextItems);
    },

    setBasketItemsUrl: (state, action: PayloadAction<{
      index: number;
      url: string;
    }>) => {
      const nextItems = updateBasketItem(
        state.basketItems,
        action.payload.index,
        { itemImageUrl: action.payload.url }
      );
      state.basketItems = nextItems;
      state.basket.items = [...nextItems];
    },

    setShortPathUpgradePrice: (state, action: PayloadAction<number>) => {
      state.shortPathUpgradePrice = action.payload;
    },

    setBasketItemsIsChecked: (state, action: PayloadAction<{
      index: number;
      value: boolean;
    }>) => {
      const nextItems = updateBasketItem(
        state.basketItems,
        action.payload.index,
        { isChecked: action.payload.value }
      );
      state.basketItems = nextItems;
      state.basket.items = [...nextItems];
    },

    resetBasketItemsIsChecked: (state) => {
      const nextItems = state.basketItems.map((item) => ({
        ...item,
        isChecked: false,
      }));
      state.basketItems = nextItems;
      state.basket.items = [...nextItems];
    },

    setDeliveryDetails: (state, action: PayloadAction<PersonalDetails>) => {
      state.basket.delivery_details = {
        ...state.basket.delivery_details,
        ...action.payload,
      };
      state.basket.invoice = {
        ...state.basket.invoice,
        details: action.payload,
      };
      state.basket.device = sessionStorage.getItem(appConst.SOURCE_DEVICE);
      state.basket.platform = sessionStorage.getItem(appConst.PLATFORM);
    },

    setDeliveryInvoiceDetails: (state, action: PayloadAction<InvoiceDetails>) => {
      if (!state.basket.invoice) {
        state.basket.invoice = {};
      }
      state.basket.invoice.details = {
        ...state.basket.invoice.details,
        ...action.payload,
      };
      state.basket.invoice.details = action.payload;
    },

    setDeliveryDetailsShop: (state, action: PayloadAction<{ shop: unknown }>) => {
      if (!state.basket.delivery_details) {
        state.basket.delivery_details = {};
      }
      state.basket.delivery_details.shop = action.payload.shop;
    },

    setDeliveryDetailsType: (state, action: PayloadAction<{ id?: string | number }>) => {
      if (!state.basket.delivery_details) {
        state.basket.delivery_details = {};
      }
      state.basket.delivery_details.delivery_type =
        action.payload.id || state.basket.delivery_details.delivery_type;
    },

    setCompanyDetails: (state, action: PayloadAction<CompanyDetails>) => {
      if (!state.basket.invoice) {
        state.basket.invoice = {};
      }
      state.basket.invoice.details = {
        ...state.basket.invoice.details,
        ...action.payload,
      };
    },

    setBasketFromCoupons: (state, action: PayloadAction<{
      payments?: Payments;
      finalPrice?: number;
      invoiceItems?: unknown[];
      items?: BasketItemFull[];
      coupons?: Coupons;
      delivery?: Delivery;
      deliveryType?: string | number;
    }>) => {
      state.basket.payments = action.payload.payments || state.basket.payments;
      if (!state.basket.delivery_details) {
        state.basket.delivery_details = {};
      }
      state.basket.delivery_details.final_price =
        action.payload.finalPrice || state.basket.delivery_details.final_price;
      if (!state.basket.invoice) {
        state.basket.invoice = {};
      }
      state.basket.invoice.items =
        action.payload.invoiceItems || state.basket.invoice.items;
      state.basket.items = action.payload.items || state.basket.items;
      state.basket.coupons = action.payload.coupons || state.basket.coupons;
      state.basket.delivery = action.payload.delivery || state.basket.delivery;
      state.basket.delivery_details.delivery_type =
        action.payload.deliveryType ||
        state.basket.delivery_details.delivery_type;
    },

    setIFrameURL: (state, action: PayloadAction<string>) => {
      state.creditGuardURL = action.payload;
    },

    setStripeText: (state, action: PayloadAction<string>) => {
      state.stripeText = action.payload;
    },

    setBasketCouponsCombinations: (state, action: PayloadAction<unknown>) => {
      if (state.basket.coupons) {
        state.basket.coupons.combinations = action.payload;
      }
    },

    setShowAlert: (state, action: PayloadAction<boolean>) => {
      state.showAlert = action.payload;
    },

    // Updates the tiles stripe config and recalculates items to reflect new thresholds.
    setTilesQuantityStripText: (state, action: PayloadAction<{
      text: string;
      max: number;
      min: number;
    }>) => {
      state.tilesQuantityStripeText = action.payload.text;
      state.tilesQuantityStripeMax = action.payload.max;
      state.tilesQuantityStripeMin = action.payload.min;
      if (state.basketItems.length > 0) {
        applyPreparedBasketItems(state, state.basketItems);
      }
    },

    setMarketingStripeVisible: (state, action: PayloadAction<boolean>) => {
      state.isMarketingStripeVisible = action.payload;
    },

    setBasketToolbarVisible: (state, action: PayloadAction<boolean>) => {
      state.isBasketToolbarVisible = action.payload;
    },

    setShippingAlertVisible: (state, action: PayloadAction<boolean>) => {
      state.isShippingAlertVisible = action.payload;
    },
  },
});

export const basketActions = basketSlice.actions;
export default basketSlice;
